import { PermissionStatus, MonitoringConfig } from "@/types/video-call";

export const DEFAULT_PERMISSION_STATUS: PermissionStatus = {
  camera: "pending",
  microphone: "pending",
  screenShare: "granted",
  monitorCheck: "passed",
};

export const DEFAULT_MONITORING_CONFIG: MonitoringConfig = {
  tabChangeDetection: false,
  externalMonitorDetection: false,
  blurDetection: false,
  viewportDetection: false,
  monitorDetectionAction: "block",
  strictMonitorMode: true,
  screenSharingEnabled: false,
  screenSharingScope: "screen",
};

export const PERMISSION_STATES = {
  PENDING: "pending" as const,
  GRANTED: "granted" as const,
  DENIED: "denied" as const,
  PASSED: "passed" as const,
  FAILED: "failed" as const,
};

export const MONITORING_ACTIONS = {
  WARNING: "warning" as const,
  PAUSE: "pause" as const,
  BLOCK: "block" as const,
};

export const DIALOG_STEPS = {
  ASSISTANT: "assistant" as const,
  MONITORING: "monitoring" as const,
};

export const AUTO_CLEANUP_THRESHOLD_MS = 5000;

export const CALL_SERVICE_ENDPOINTS = {
  INITIATE: "/api/call",
  END: (callId: string) => `/api/call/${callId}`,
};

export const LIVEKIT_EVENTS = {
  CONNECTED: "connected",
  DISCONNECTED: "disconnected",
  PARTICIPANT_CONNECTED: "participantConnected",
  PARTICIPANT_DISCONNECTED: "participantDisconnected",
  TRACK_SUBSCRIBED: "trackSubscribed",
  TRACK_UNSUBSCRIBED: "trackUnsubscribed",
  DATA_RECEIVED: "dataReceived",
};

export const TAB_CHANGE_MONITORING = {
  EVENTS: {
    BLUR: "blur",
    FOCUS: "focus",
    VISIBILITY_CHANGE: "visibilitychange",
    PAGE_HIDE: "pagehide",
    PAGE_SHOW: "pageshow",
    BEFORE_UNLOAD: "beforeunload",
  },
};

export const SIRI_WAVE_CONFIG = {
  container: null,
  width: 400,
  height: 100,
  style: "ios9" as const,
  ratio: 1,
  speed: 0.2,
  amplitude: 1,
  frequency: 6,
  color: "#fff",
  cover: false,
  autostart: false,
  pixelDepth: 0.02,
  lerpSpeed: 0.01,
};

export const getScreenShareConfig = (echoCancellation: boolean = true) => ({
  video: {
    cursor: "always" as const,
    displaySurface: "screen" as const,
    logicalSurface: true,
  },
  audio: {
    echoCancellation, 
    noiseSuppression: true,
    autoGainControl: true,
  },
});

export const SCREEN_SHARE_CONFIG = getScreenShareConfig();

export const getMediaConstraints = (echoCancellation: boolean = true) => ({
  video: {
    width: { ideal: 1920, min: 1280, max: 3840 },
    height: { ideal: 1080, min: 720, max: 2160 },
    frameRate: { ideal: 30, min: 24, max: 60 },
    facingMode: "user",
  },
  audio: {
    echoCancellation,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate: { ideal: 48000 },
    channelCount: { ideal: 2 },
  },
});

export const MEDIA_CONSTRAINTS = getMediaConstraints();

export const EARPHONE_DETECTION_CONFIG = {
  // Tone burst configuration
  TEST_FREQUENCIES: [440, 880, 1320], // Test tone frequencies in Hz
  TONE_DURATION: 200, // Duration of each tone in ms
  TONE_SPACING: 300, // Time between tones in ms
  
  // Timing configuration in milliseconds
  BASELINE_DURATION: 300, // Time to measure background noise
  ANALYSIS_DURATION: 200, // Time for final analysis
  TOTAL_TEST_DURATION: 1800, // Total test time (baseline + 3 tones + analysis)
  
  // Detection thresholds (calibrated for real-world use)
  ECHO_THRESHOLD_DB: 2.5, // dB above baseline to detect speakers (lowered from 6dB)
  CONFIDENCE_THRESHOLD: 0.5, // Minimum confidence for definitive result
  LOW_CONFIDENCE_THRESHOLD: 0.3, // Threshold for inconclusive results
  PRIMARY_TONE_THRESHOLD: 2.0, // First tone threshold (most reliable)
  
  // Audio configuration for test
  TEST_AUDIO_CONFIG: {
    echoCancellation: false, // CRITICAL: Must be false for detection
    noiseSuppression: false,
    autoGainControl: false,
    sampleRate: 44100,
  },
  
  // Analyzer configuration
  FFT_SIZE: 2048, // FFT size for frequency analysis
  SMOOTHING_TIME_CONSTANT: 0.3, // Analyzer smoothing
  MIN_DECIBELS: -100,
  MAX_DECIBELS: -30,
  
  // Test volume and safety
  TEST_VOLUME: 0.2, // Volume level for test tones (0-1) - Reduced to protect earphone users
  ENVELOPE_RAMP_TIME: 0.01, // Ramp time to avoid clicks (seconds)
  
  // Retry and timeout configuration
  MAX_RETRY_ATTEMPTS: 2,
  TEST_TIMEOUT_MS: 15000, // Maximum time for entire test
  
  // Detection sensitivity levels
  SENSITIVITY: {
    HIGH: { echoThreshold: 1.5, confidenceThreshold: 0.7 },   // Very sensitive
    MEDIUM: { echoThreshold: 2.5, confidenceThreshold: 0.5 }, // Balanced (default)  
    LOW: { echoThreshold: 4.0, confidenceThreshold: 0.4 },    // Less sensitive
  },
};

// Transcription-based earphone detection configuration
export const TRANSCRIPTION_EARPHONE_CONFIG = {
  // Test phrase - clear and distinct words for transcription accuracy
  TEST_PHRASE: "Testing audio one two three four five",
  
  // Detection thresholds
  MATCH_THRESHOLD: 0.6, // 60% similarity indicates speakers (audio leakage)
  EARPHONE_THRESHOLD: 0.2, // <20% similarity indicates earphones (no leakage)
  
  // Timing configuration
  TRANSCRIPTION_TIMEOUT: 5000, // 5 seconds to capture audio
  SPEECH_DELAY: 500, // Delay before starting transcription after speech starts
  
  // Audio playback settings
  AUDIO_PLAYBACK_VOLUME: 0.3, // 30% volume for test
  USE_SPEECH_SYNTHESIS: true, // Use browser Speech Synthesis API
  
  // Speech Synthesis settings
  SPEECH_SYNTHESIS_SETTINGS: {
    rate: 0.9, // Slightly slower for clarity
    pitch: 1.0, // Normal pitch
    volume: 0.3, // Match playback volume
    lang: 'en-US'
  },
  
  // Web Speech API settings
  SPEECH_RECOGNITION_SETTINGS: {
    continuous: false,
    interimResults: false,
    maxAlternatives: 1,
    lang: 'en-US'
  },
  
  // Audio constraints for recording (echo cancellation OFF)
  RECORDING_CONSTRAINTS: {
    echoCancellation: false, // CRITICAL: Must be false to detect audio leakage
    noiseSuppression: false, // Disable to capture all audio
    autoGainControl: false,
    sampleRate: 44100,
  },
  
  // Retry configuration
  MAX_RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second between retries
};

// Deepgram-based earphone detection configuration
export const DEEPGRAM_EARPHONE_CONFIG = {
  // Test phrase - same as transcription config for consistency
  TEST_PHRASE: "Testing audio one two three four five",
  
  // Detection thresholds
  SPEAKERS_THRESHOLD: 0.60, // >60% similarity indicates speakers (audio leakage)
  EARPHONES_THRESHOLD: 0.20, // <20% similarity indicates earphones (no leakage)
  MIN_CONFIDENCE_THRESHOLD: 0.3, // Minimum Deepgram confidence to trust results
  
  // Deepgram configuration
  DEEPGRAM_URL: 'wss://api.deepgram.com/v1/listen',
  DEEPGRAM_MODEL: 'nova-3',
  DEEPGRAM_LANGUAGE: 'en-US',
  
  // Audio settings
  AUDIO_VOLUME: 1.0, // High volume for better detection
  AUDIO_RATE: 0.7, // Slower speech for better recognition
  AUDIO_PITCH: 1.0,
  
  // Timing configuration
  STARTUP_DELAY: 2000, // 2s delay before playing audio
  PROCESSING_DELAY: 4000, // 4s to wait after audio ends
  MAX_TEST_DURATION: 12000, // 12s total test duration
  MEDIA_RECORDER_INTERVAL: 100, // Send audio data every 100ms
  
  // Audio constraints - echo cancellation OFF for detection
  AUDIO_CONSTRAINTS: {
    echoCancellation: false, // CRITICAL: Must be false to detect audio leakage
    noiseSuppression: false, // Disable to capture browser audio
    autoGainControl: false, // Disable to maintain consistent levels
    channelCount: 1, // Mono audio for efficiency
    sampleRate: 16000, // 16kHz for good quality/bandwidth balance
  },
  
  // Error handling
  MAX_RETRY_ATTEMPTS: 3,
  WEBSOCKET_TIMEOUT: 5000, // 5s timeout for WebSocket connection
  
  // UI messages
  MESSAGES: {
    CONNECTING: 'Connecting to audio verification service...',
    LISTENING: 'Listening for audio...',
    PLAYING: 'Playing test audio...',
    ANALYZING: 'Analyzing audio feedback...',
    LOW_VOLUME: 'Audio too quiet - please increase volume and retry',
    NETWORK_ERROR: 'Connection failed - please check your internet and retry',
  }
};

// Interview-specific audio constraints (echo cancellation disabled)
export const INTERVIEW_AUDIO_CONSTRAINTS = {
  echoCancellation: false, // Disabled to enforce earphone usage
  noiseSuppression: true, // Keep noise suppression for quality
  autoGainControl: true, // Keep AGC for consistent levels
  sampleRate: { ideal: 48000 },
  channelCount: { ideal: 2 },
};

export const VIDEO_CALL_END_REASONS = {
  // User-initiated endings
  USER_ENDED: "user-ended-call",
  USER_ENDED_DURING_RING: "user-ended-call-during-ring",
  
  // Assistant/System-initiated endings
  ASSISTANT_ENDED: "assistant-ended-web-session",
  ASSISTANT_ENDED_WEB_SESSION: "assistant-ended-web-session",
  INTERVIEW_COMPLETED: "interview-completed",
  TIME_LIMIT_REACHED: "time-limit-reached",
  
  // Violation-based endings
  TAB_CHANGE_VIOLATION: "tab-change-violation-limit",
  WINDOW_BLUR_VIOLATION: "window-blur-violation-limit",
  TOTAL_VIOLATION: "violation-limit-reached",
  MONITOR_VIOLATION: "external-monitor-violation",
  CAMERA_VIOLATION: "camera-violation",
  SCREENSHARE_VIOLATION: "screenshare-violation",
  FACE_DETECTION_VIOLATION: "face-detection-violation",
  WINDOW_MANIPULATION_VIOLATION: "window-manipulation-violation",
  FULLSCREEN_EXIT_VIOLATION: "fullscreen-exit-violation",
  
  // Technical/Network issues
  NETWORK_DISCONNECTED: "network-disconnected",
  NETWORK_CONNECTION_LOST: "network-connection-lost",
  SERVER_SHUTDOWN: "server-shutdown",
  ROOM_DELETED: "room-deleted",
  PARTICIPANT_REMOVED: "participant-removed",
  CONNECTION_FAILED: "connection-failed",
  TECHNICAL_ISSUES: "technical-issues",
  
  // Media-related endings
  CAMERA_FAILED: "camera-failed",
  SCREENSHARE_FAILED: "screenshare-failed",
  AUDIO_FAILED: "audio-failed",
  
  // Default/Unknown
  UNKNOWN: "unknown",
} as const;

export type VideoCallEndReason = (typeof VIDEO_CALL_END_REASONS)[keyof typeof VIDEO_CALL_END_REASONS];